/**
 * /academy/article/:slug: breadcrumbs, title and meta, summary, cover, the MDX body (intro, CTA, key
 * takeaways, sections, conclusion), risk reminder, related reading, FAQs and the previous / next
 * lesson, with a sticky table of contents and editor's picks beside it on desktop.
 */
import { useEffect, useRef, useState } from 'react';
import { cx, Icon, Spinner } from '../../../components/ui';
import { Link } from '../../../lib/router';
import { formatDate, lessonNav, relatedArticles, sortPopular } from './catalog';
import { ARTICLES, articleBySlug, loadArticle, loadedArticle } from './content';
import { usePageHead } from './head';
import { NotFound } from './Listings';
import { FAQ, MDX_COMPONENTS, RiskReminder } from './mdx';
import { markRead, useReadSet } from './progress';
import { learnPath } from './routes';
import { categoryInfo, courseInfo, topicInfo } from './taxonomy';
import type { ArticleMeta, ArticleModule } from './types';
import { Cover, Crumbs, LevelBadge } from './ui';

interface TocItem {
  id: string;
  text: string;
  depth: 2 | 3;
}

/** Headings of the rendered body, and the one being read (the last whose top has passed under the bar). */
function useToc(body: HTMLElement | null, ready: boolean) {
  const [items, setItems] = useState<TocItem[]>([]);
  const [active, setActive] = useState<string | null>(null);
  useEffect(() => {
    if (!body || !ready) return;
    const heads = [...body.querySelectorAll<HTMLElement>('h2[id], h3[id]')].filter((h) => !h.closest('aside'));
    setItems(heads.map((h) => ({ id: h.id, text: h.textContent ?? '', depth: h.tagName === 'H3' ? 3 : 2 })));
    let frame = 0;
    const update = () => {
      frame = 0;
      const current = [...heads].reverse().find((h) => h.getBoundingClientRect().top < 130);
      setActive(current?.id ?? heads[0]?.id ?? null);
    };
    // Capture: the page scrolls in its own area on desktop and as the document on phones.
    const onScroll = () => (frame ||= requestAnimationFrame(update));
    update();
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      cancelAnimationFrame(frame);
    };
  }, [body, ready]);
  return { items, active };
}

function jump(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  history.replaceState(history.state, '', `#${id}`);
}

function Toc({ items, active, onPick }: { items: TocItem[]; active: string | null; onPick?: () => void }) {
  return (
    <ol className="m-0 flex list-none flex-col gap-0.5 border-l border-line p-0">
      {items.map((it) => (
        <li key={it.id}>
          <a
            href={`#${it.id}`}
            onClick={(e) => {
              e.preventDefault();
              jump(it.id);
              onPick?.();
            }}
            aria-current={active === it.id ? 'location' : undefined}
            className={cx(
              '-ml-px block border-l-2 py-1.5 pr-2 text-[13.5px] leading-snug no-underline',
              it.depth === 3 ? 'pl-7' : 'pl-4',
              active === it.id ? 'border-accent font-semibold text-fg' : 'border-transparent text-muted hover:text-fg',
            )}
          >
            {it.text}
          </a>
        </li>
      ))}
    </ol>
  );
}

function EditorsPicks({ current }: { current: string }) {
  const picks = sortPopular(ARTICLES)
    .filter((a) => a.slug !== current)
    .slice(0, 4);
  return (
    <section className="flex flex-col gap-3">
      <h2 className="m-0 text-[13px] font-semibold tracking-[0.08em] text-muted uppercase">Editor’s picks</h2>
      {picks.map((a) => (
        <Link key={a.slug} to={learnPath.article(a.slug)} className="group flex items-start gap-3 text-fg no-underline">
          <img src={a.cover} alt="" loading="lazy" decoding="async" className="aspect-video w-[92px] shrink-0 rounded-lg bg-track object-cover" />
          <span className="flex min-w-0 flex-col gap-1">
            <span className="line-clamp-2 text-[13.5px] leading-snug font-semibold group-hover:text-accent-fg">{a.title}</span>
            <span className="text-[12px] text-muted">{a.readTime} min</span>
          </span>
        </Link>
      ))}
    </section>
  );
}

function LessonLink({ article, dir }: { article: ArticleMeta | null; dir: 'prev' | 'next' }) {
  if (!article) return <div className="hidden sm:block" />;
  return (
    <Link
      to={learnPath.article(article.slug)}
      className={cx('group flex flex-col gap-1.5 rounded-[16px] border border-line p-4 text-fg no-underline hover:border-line-strong hover:bg-panel', dir === 'next' && 'sm:items-end sm:text-right')}
    >
      <span className="inline-flex items-center gap-1 text-[12.5px] font-semibold tracking-[0.06em] text-muted uppercase">
        {dir === 'prev' && <Icon name="left" size={13} />}
        {dir === 'prev' ? 'Previous lesson' : 'Next lesson'}
        {dir === 'next' && <Icon name="right" size={13} />}
      </span>
      <span className="text-[15.5px] leading-snug font-semibold group-hover:text-accent-fg">{article.title}</span>
    </Link>
  );
}

function Skeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-3 py-6">
      {[96, 88, 92, 70, 84, 60].map((w, i) => (
        <div key={i} className="h-4 animate-pulse rounded bg-track" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

export function ArticlePage({ slug }: { slug: string }) {
  const meta = articleBySlug(slug);
  const read = useReadSet();
  const [mod, setMod] = useState<ArticleModule | null>(() => loadedArticle(slug));
  const [err, setErr] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [body, setBody] = useState<HTMLDivElement | null>(null);
  const end = useRef<HTMLDivElement>(null);
  usePageHead({ kind: 'article', slug }, mod?.faqs);

  useEffect(() => {
    let live = true;
    setErr(null);
    setMod(loadedArticle(slug));
    if (articleBySlug(slug))
      loadArticle(slug).then(
        (m) => live && setMod(m),
        () => live && setErr('This article could not be loaded. Check your connection and try again.'),
      );
    return () => {
      live = false;
    };
  }, [slug, attempt]);

  // A #section link opens at that section once the body is there.
  useEffect(() => {
    if (!mod) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id) requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
  }, [mod]);

  // Read to the end: counts toward the learning path.
  useEffect(() => {
    const el = end.current;
    if (!mod || !el) return;
    const io = new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && markRead(slug));
    io.observe(el);
    return () => io.disconnect();
  }, [mod, slug]);

  const { items, active } = useToc(body, !!mod);

  if (!meta) return <NotFound what="Article" />;
  const course = courseInfo(meta.course)!;
  const category = categoryInfo(meta.category)!;
  const nav = lessonNav(ARTICLES, meta);
  const related = relatedArticles(ARTICLES, meta);
  const Body = mod?.default;

  return (
    <div className="grid gap-10 pt-6 pb-4 lg:grid-cols-[minmax(0,1fr)_280px] xl:grid-cols-[minmax(0,1fr)_300px] xl:gap-16">
      <article className="min-w-0 lg:max-w-[780px]">
        <Crumbs items={[{ to: learnPath.home(), label: 'Academy' }, { to: learnPath.course(course.id), label: course.title }, { label: meta.title }]} />
        <h1 className="m-0 mt-5 text-[clamp(30px,4.6vw,46px)] leading-[1.1] font-semibold tracking-tight text-balance">{meta.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13.5px] text-muted">
          <LevelBadge level={meta.level} />
          <Link to={learnPath.course(course.id)} className="font-medium text-fg no-underline hover:text-accent-fg">
            {course.title} · Lesson {meta.order} of {Math.max(nav.total, meta.order)}
          </Link>
          <span aria-hidden="true">·</span>
          <Link to={learnPath.category(category.id)} className="text-muted no-underline hover:text-fg">
            {category.title}
          </Link>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1">
            <Icon name="docs" size={13} /> {meta.readTime} min read
          </span>
          <span aria-hidden="true">·</span>
          <span>
            Published <time dateTime={meta.publishedAt}>{formatDate(meta.publishedAt)}</time>
          </span>
          {meta.updatedAt !== meta.publishedAt && (
            <>
              <span aria-hidden="true">·</span>
              <span>
                Updated <time dateTime={meta.updatedAt}>{formatDate(meta.updatedAt)}</time>
              </span>
            </>
          )}
        </div>
        <p className="m-0 mt-6 text-[19px] leading-relaxed text-fg/80">{meta.description}</p>
        <Cover article={meta} eager className="mt-8 rounded-[18px]" />
        {items.length > 2 && (
          <details className="group mt-6 rounded-[14px] border border-line bg-panel lg:hidden">
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-[14.5px] font-semibold [&::-webkit-details-marker]:hidden">
              On this page
              <Icon name="chevron" size={15} className="text-muted transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-4 pb-4">
              <Toc items={items} active={active} />
            </div>
          </details>
        )}

        <div ref={setBody} className="mt-6">
          {Body ? (
            <Body components={MDX_COMPONENTS} />
          ) : err ? (
            <div className="my-8 flex flex-col items-start gap-3 rounded-[14px] border border-line bg-panel p-5">
              <p className="m-0 text-[15px]">{err}</p>
              <button type="button" onClick={() => setAttempt((n) => n + 1)} className="h-10 rounded-full bg-fg px-4 text-[14px] font-semibold text-bg">
                Try again
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 pt-6 text-muted">
                <Spinner /> Loading the article…
              </div>
              <Skeleton />
            </>
          )}
        </div>
        <div ref={end} aria-hidden="true" />

        <RiskReminder />

        <section aria-labelledby="related-title" className="mt-12">
          <h2 id="related-title" className="m-0 mb-4 text-[22px] font-semibold tracking-tight">
            Related reading
          </h2>
          <ol className="m-0 flex list-none flex-col gap-0 overflow-hidden rounded-[16px] border border-line p-0">
            {related.map((r, i) => (
              <li key={r.slug} className="border-b border-line last:border-b-0">
                <Link to={learnPath.article(r.slug)} className="group flex items-center gap-4 px-4 py-3.5 text-fg no-underline hover:bg-panel">
                  <span className="num w-6 shrink-0 text-[15px] font-semibold text-muted">{String(i + 1).padStart(2, '0')}</span>
                  <span className="min-w-0 flex-1 text-[15px] font-medium group-hover:text-accent-fg">{r.title}</span>
                  {read.has(r.slug) && <Icon name="check" size={15} className="shrink-0 text-up" />}
                  <LevelBadge level={r.level} className="hidden shrink-0 sm:inline-flex" />
                </Link>
              </li>
            ))}
          </ol>
        </section>

        {mod && mod.faqs.length > 0 && (
          <section aria-labelledby="faq-title" className="mt-12">
            <h2 id="faq-title" className="m-0 mb-4 text-[22px] font-semibold tracking-tight">
              FAQs
            </h2>
            <FAQ items={mod.faqs} />
          </section>
        )}

        <nav aria-label="Lessons" className="mt-12 grid gap-3 sm:grid-cols-2">
          <LessonLink article={nav.prev} dir="prev" />
          <LessonLink article={nav.next} dir="next" />
        </nav>

        <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-line pt-6">
          <span className="mr-1 text-[13px] text-muted">Topics</span>
          {meta.topics.map((t) => (
            <Link key={t} to={learnPath.topic(t)} className="rounded-full bg-pill px-3 py-1.5 text-[13px] font-medium text-fg no-underline hover:bg-pill-on">
              {topicInfo(t)?.title ?? t}
            </Link>
          ))}
        </div>
      </article>

      <aside className="hidden lg:block">
        <div className="sticky top-24 flex max-h-[calc(100vh-7rem)] flex-col gap-8 overflow-y-auto pb-6 no-scrollbar">
          {items.length > 0 && (
            <nav aria-label="On this page" className="flex flex-col gap-3">
              <h2 className="m-0 text-[13px] font-semibold tracking-[0.08em] text-muted uppercase">On this page</h2>
              <Toc items={items} active={active} />
            </nav>
          )}
          <EditorsPicks current={meta.slug} />
        </div>
      </aside>
    </div>
  );
}
